<script setup lang="ts">
import { ref, computed } from 'vue'

const props = defineProps<{
  headerText?: string
  bodyText?: string[]
  footerText?: string
  position?: string
  offset?: number
  /** Cor de fundo do tooltip (hex, rgb, etc) */
  backgroundColor?: string
  /** Cor do texto (opcional) */
  textColor?: string
}>()

const isHovered = ref(false)
const triggerRect = ref<DOMRect>()
const OFFSET = computed(() => props.offset ?? 8)

const showTooltip = (event: Event) => {
  const el = event.currentTarget as HTMLElement
  triggerRect.value = el.getBoundingClientRect()
  isHovered.value = true
}

const hideTooltip = () => {
  triggerRect.value = undefined
  isHovered.value = false
}

const hasText = computed(() =>
  Boolean(props.headerText || props.bodyText?.length || props.footerText)
)

const isOnRightSide = computed(
  () => Number(triggerRect.value?.x || 0) / window.innerWidth > 0.5
)

const isOnBottom = computed(
  () => Number(triggerRect.value?.y || 0) / window.innerHeight > 0.5
)

const computedStyles = computed(() => {
  if (!triggerRect.value) return {}

  const vw = window.innerWidth
  const vh = window.innerHeight
  const rect = triggerRect.value

  const top = isOnBottom.value ? '' : `${rect.bottom + OFFSET.value}px`
  const bottom = isOnBottom.value ? `${vh - rect.top + OFFSET.value}px` : ''

  const leftRaw = rect.left
  const rightRaw = vw - rect.right

  const left = !isOnRightSide.value ? `${Math.max(8, leftRaw)}px` : ''
  const right = isOnRightSide.value ? `${Math.max(8, rightRaw)}px` : ''

  return {
    top,
    bottom,
    left,
    right,
    maxWidth: '430px',
    zIndex: '2147483647',
  } as Record<string, string>
})

const bgColor = computed(() => props.backgroundColor ?? 'rgba(97, 97, 97, 0.9)')
const fgColor = computed(() => props.textColor ?? '#FFFFFF')
</script>

<template>
  <div
    :class="{ 'cursor-pointer': hasText }"
    @mouseover="showTooltip"
    @mouseleave="hideTooltip"
  >
    <slot />

    <teleport to="body">
      <div
        v-if="isHovered && hasText"
        class="text-body-2 pa-2 position-fixed elevation-12 rounded"
        :style="{
          ...computedStyles,
          backgroundColor: bgColor,
          color: fgColor,
        }"
        style="pointer-events: none"
      >
        <div
          v-if="props.headerText"
          class="mb-2"
          style="font-weight: 400; opacity: 0.95"
          v-html="props.headerText"
        />

        <h3
          v-for="(text, index) in props.bodyText"
          :key="index"
          class="text-body-2 mb-2"
        >
          {{ text }}
        </h3>

        <div v-if="props.footerText" class="mt-1" style="opacity: 0.85">
          {{ props.footerText }}
        </div>
      </div>
    </teleport>
  </div>
</template>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
